// SuperAdminDashboard.jsx — with Contacts section
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "../config";

const timeAgo = (isoString) => {
  if (!isoString) return "—";
  const diff = Math.floor((Date.now() - new Date(isoString).getTime()) / 1000);
  if (diff < 5)       return "just now";
  if (diff < 60)      return `${diff}s ago`;
  if (diff < 3600)    return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400)   return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800)  return `${Math.floor(diff / 86400)}d ago`;
  if (diff < 2592000) return `${Math.floor(diff / 604800)}w ago`;
  return new Date(isoString).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
};

const fmtDate = (v) =>
  v ? new Date(v).toLocaleString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

const parseAction = (action = "") => {
  const colon = action.indexOf(":");
  if (colon === -1) return { verb: action.trim(), detail: null };
  return { verb: action.slice(0, colon).trim(), detail: action.slice(colon + 1).trim() || null };
};

const actionStyle = (action = "") => {
  const a = action.toUpperCase();
  if (a.startsWith("LOGIN"))                               return { bg: "#eff6ff", c: "#1d4ed8" };
  if (a.startsWith("LOGOUT"))                              return { bg: "#f3f4f6", c: "#4b5563" };
  if (a.startsWith("ADD_") || a.startsWith("ACCEPT_"))    return { bg: "#f0fdf4", c: "#15803d" };
  if (a.startsWith("EDIT_") || a.startsWith("UPDATE_"))   return { bg: "#fffbeb", c: "#b45309" };
  if (a.startsWith("DELETE_") || a.startsWith("REMOVE_")) return { bg: "#fef2f2", c: "#b91c1c" };
  if (a.startsWith("ASSIGN_") || a.startsWith("LINK_"))   return { bg: "#f5f3ff", c: "#6d28d9" };
  if (a.includes("ACTIVATED"))                            return { bg: "#f0fdf4", c: "#15803d" };
  if (a.includes("DEACTIVATED"))                          return { bg: "#fef2f2", c: "#b91c1c" };
  return { bg: "#f3f4f6", c: "#6b7280" };
};

const verbLabel = (verb = "") => {
  const map = {
    ADD_PATIENT:"Added patient",EDIT_PATIENT:"Edited patient",DELETE_PATIENT:"Deleted patient",
    ADD_DOCTOR:"Added doctor",EDIT_DOCTOR:"Edited doctor",DELETE_DOCTOR:"Deleted doctor",
    ADD_HOSPITAL:"Added hospital",EDIT_HOSPITAL:"Edited hospital",DELETE_HOSPITAL:"Deleted hospital",
    ADD_ADMIN:"Added admin",EDIT_ADMIN:"Edited admin",DELETE_ADMIN:"Deleted admin",
    ADD_DEVICE:"Added device",EDIT_DEVICE:"Edited device",DELETE_DEVICE:"Deleted device",
    ASSIGN_DEVICE:"Assigned device",LINK_DEVICE:"Linked device",REMOVE_DEVICE:"Removed device",
    HOSPITAL_ACTIVATED:"Activated hospital",HOSPITAL_DEACTIVATED:"Deactivated hospital",
    ADMIN_ACTIVATED:"Activated admin",ADMIN_DEACTIVATED:"Deactivated admin",
    LOGIN:"Logged in",LOGOUT:"Logged out",ACCEPT_REQUEST:"Accepted request",
  };
  return map[verb.toUpperCase()] || verb.replace(/_/g, " ").toLowerCase().replace(/\b\w/, (c) => c.toUpperCase());
};

// ── Subject badge colours ───────────────────────────────────────────────────
const subjectStyle = (subject = "") => {
  const map = {
    "Technical Support":    { bg: "#eff6ff", c: "#1d4ed8" },
    "Device Integration":   { bg: "#f5f3ff", c: "#6d28d9" },
    "Sales Inquiry":        { bg: "#f0fdf4", c: "#15803d" },
    "Clinical Partnership": { bg: "#fff7ed", c: "#c2410c" },
    "Other":                { bg: "#f3f4f6", c: "#4b5563" },
  };
  return map[subject] || { bg: "#f3f4f6", c: "#6b7280" };
};

const statusStyle = (s = "") => {
  if (s === "unread")   return { bg: "#eff6ff", c: "#1d4ed8" };
  if (s === "read")     return { bg: "#f0fdf4", c: "#15803d" };
  if (s === "archived") return { bg: "#f3f4f6", c: "#6b7280" };
  return { bg: "#f3f4f6", c: "#6b7280" };
};

const NAV = [
  { id: "overview",  icon: "dashboard",            label: "Overview"    },
  { id: "hospitals", icon: "local_hospital",       label: "Hospitals"   },
  { id: "admins",    icon: "admin_panel_settings", label: "Admins"      },
  { id: "devices",   icon: "devices",              label: "Devices"     },
  { id: "contacts",  icon: "mail",                 label: "Contacts"    },
  { id: "audit",     icon: "assignment",           label: "Audit Trail" },
];

// ═══════════════════════════════════════════════════════════════════════════
const SuperAdminDashboard = () => {
  const navigate  = useNavigate();
  const actorId   = localStorage.getItem("userId");
  const actorName = localStorage.getItem("username") || "superadmin";
  const withActor = (body = {}) => ({ ...body, _actorId: actorId, _actorName: actorName });

  const handleLogout = async () => {
    const loginTime  = parseInt(localStorage.getItem("loginTime") || "0");
    const activeTime = loginTime ? Math.floor((Date.now() - loginTime) / 1000) : null;
    await fetch(`${API}/logout`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ userId: actorId, username: actorName, activeTime }),
    }).catch(() => {});
    localStorage.clear();
    navigate("/signin");
  };

  const [page, setPage] = useState("overview");

  // ── Core state ─────────────────────────────────────────────────────────
  const [hospitals,    setHospitals]    = useState([]);
  const [admins,       setAdmins]       = useState([]);
  const [auditLogs,    setAuditLogs]    = useState([]);
  const [auditTotal,   setAuditTotal]   = useState(0);
  const [auditPage,    setAuditPage]    = useState(1);
  const [auditSearch,  setAuditSearch]  = useState("");
  const [auditLoading, setAuditLoading] = useState(false);
  const auditPerPage = 50;

  const [devices,       setDevices]       = useState([]);
  const [deviceTotal,   setDeviceTotal]   = useState(0);
  const [devicePage,    setDevicePage]    = useState(1);
  const [deviceSearch,  setDeviceSearch]  = useState("");
  const [deviceLoading, setDeviceLoading] = useState(false);
  const devicesPerPage = 20;

  // ── Contacts state ─────────────────────────────────────────────────────
  const [contacts,        setContacts]        = useState([]);
  const [contactTotal,    setContactTotal]    = useState(0);
  const [contactPage,     setContactPage]     = useState(1);
  const [contactSearch,   setContactSearch]   = useState("");
  const [contactFilter,   setContactFilter]   = useState("");   // "" | "unread" | "read" | "archived"
  const [contactLoading,  setContactLoading]  = useState(false);
  const [viewContact,     setViewContact]     = useState(null); // full-detail modal
  const contactsPerPage = 20;

  const [hospSearch,  setHospSearch]  = useState("");
  const [adminSearch, setAdminSearch] = useState("");

  // ── Modals ─────────────────────────────────────────────────────────────
  const [hospitalModal,   setHospitalModal]   = useState(false);
  const [editingHospital, setEditingHospital] = useState(null);
  const [viewHospModal,   setViewHospModal]   = useState(null);
  const [hospitalForm,    setHospitalForm]    = useState({ name: "", address: "", contact_email: "", contact_phone: "", status: "active", is_active: true, device_count: 0, admin_id: "" });

  const [adminModal,     setAdminModal]     = useState(false);
  const [editingAdmin,   setEditingAdmin]   = useState(null);
  const [viewAdminModal, setViewAdminModal] = useState(null);
  const [adminForm,      setAdminForm]      = useState({ username: "", email: "", role: "admin", hospital_id: "", is_active: false });

  const [deviceModal,   setDeviceModal]   = useState(false);
  const [editingDevice, setEditingDevice] = useState(null);
  const [deviceForm,    setDeviceForm]    = useState({ uid: "", hospital_id: "", status: "offline" });

  const [auditDetailModal, setAuditDetailModal] = useState(null);

  // ── Fetch helpers ──────────────────────────────────────────────────────
  const fetchHospitals = async () => {
    try { const r = await fetch(`${API}/hospitals`); const d = await r.json(); setHospitals(Array.isArray(d) ? d : d.hospitals ?? []); }
    catch { setHospitals([]); }
  };

  const fetchAdmins = async () => {
    try { const r = await fetch(`${API}/admins`); const d = await r.json(); setAdmins(Array.isArray(d) ? d : []); }
    catch { setAdmins([]); }
  };

  const fetchAuditLogs = useCallback(async (pg = 1, search = "") => {
    setAuditLoading(true);
    try {
      const p = new URLSearchParams({ page: pg, limit: auditPerPage, search });
      const r = await fetch(`${API}/audit-logs?${p}`);
      const d = await r.json();
      setAuditLogs(d.logs || []); setAuditTotal(d.total || 0);
    } catch {} finally { setAuditLoading(false); }
  }, []);

  const fetchDevices = useCallback(async (pg = 1, search = "") => {
    setDeviceLoading(true);
    try {
      const p = new URLSearchParams({ page: pg, limit: devicesPerPage, search });
      const r = await fetch(`${API}/devices?${p}`);
      const d = await r.json();
      setDevices(d.devices || []); setDeviceTotal(d.total || 0);
    } catch {} finally { setDeviceLoading(false); }
  }, []);

  const fetchContacts = useCallback(async (pg = 1, search = "", status = "") => {
    setContactLoading(true);
    try {
      const p = new URLSearchParams({ page: pg, limit: contactsPerPage });
      if (search) p.set("search", search);
      if (status) p.set("status", status);
      const r = await fetch(`${API}/contact?${p}`);
      const d = await r.json();
      setContacts(d.data || []); setContactTotal(d.total || 0);
    } catch {} finally { setContactLoading(false); }
  }, []);

  useEffect(() => {
    fetchHospitals(); fetchAdmins(); fetchAuditLogs(); fetchDevices(); fetchContacts();
  }, []);
  useEffect(() => { fetchAuditLogs(auditPage, auditSearch); }, [auditPage, auditSearch]);
  useEffect(() => { fetchDevices(devicePage, deviceSearch); }, [devicePage, deviceSearch]);
  useEffect(() => { fetchContacts(contactPage, contactSearch, contactFilter); }, [contactPage, contactSearch, contactFilter]);

  // ── Hospital actions ───────────────────────────────────────────────────
  const openAddHosp  = () => { setEditingHospital(null); setHospitalForm({ name: "", address: "", contact_email: "", contact_phone: "", status: "active", is_active: true, device_count: 0, admin_id: "" }); setHospitalModal(true); };
  const openEditHosp = (h) => { setEditingHospital(h); setHospitalForm({ name: h.name, address: h.address, contact_email: h.contact_email, contact_phone: h.contact_phone, status: h.status, is_active: !!h.is_active, device_count: h.device_count, admin_id: h.admin_id ?? "" }); setHospitalModal(true); };

  const submitHosp = async (e) => {
    e.preventDefault();
    const body = withActor({ ...hospitalForm, admin_id: hospitalForm.admin_id ? Number(hospitalForm.admin_id) : null, device_count: Number(hospitalForm.device_count) });
    const res  = await fetch(editingHospital ? `${API}/hospitals/${editingHospital.id}` : `${API}/hospitals`, { method: editingHospital ? "PUT" : "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
    if (!res.ok) { const d = await res.json(); alert(d.error || "Failed"); return; }
    setHospitalModal(false); fetchHospitals(); fetchAdmins(); fetchAuditLogs(1, auditSearch);
  };

  const toggleHospitalActivation = async (h) => {
    const newState = !h.is_active;
    if (!window.confirm(`${newState ? "Activate" : "Deactivate"} hospital "${h.name}"?`)) return;
    const res = await fetch(`${API}/hospitals/${h.id}/toggle-activation`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify(withActor({ is_active: newState })),
    });
    if (!res.ok) { const d = await res.json(); alert(d.error || "Failed"); return; }
    fetchHospitals(); fetchAdmins(); fetchAuditLogs(1, auditSearch);
  };

  const deleteHosp = async (id) => {
    if (!window.confirm("Delete hospital?")) return;
    await fetch(`${API}/hospitals/${id}`, { method: "DELETE", headers: { "Content-Type": "application/json" }, body: JSON.stringify(withActor({})) });
    fetchHospitals(); fetchAdmins();
  };

  // ── Admin actions ──────────────────────────────────────────────────────
  const openAddAdmin  = () => { setEditingAdmin(null); setAdminForm({ username: "", email: "", role: "admin", hospital_id: "", is_active: false }); setAdminModal(true); };
  const openEditAdmin = (a) => { setEditingAdmin(a); setAdminForm({ username: a.username, email: a.email, role: a.role, hospital_id: a.hospital_id ?? "", is_active: !!a.is_active }); setAdminModal(true); };

  const submitAdmin = async (e) => {
    e.preventDefault();
    const body = withActor({ ...adminForm, hospital_id: adminForm.hospital_id ? Number(adminForm.hospital_id) : null });
    const res  = await fetch(editingAdmin ? `${API}/admins/${editingAdmin.id}` : `${API}/admins`, { method: editingAdmin ? "PUT" : "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
    if (!res.ok) { const d = await res.json(); alert(d.error || "Failed"); return; }
    if (!editingAdmin) {
      const d = await res.json();
      if (d.temporaryPassword) alert(`Admin created!\n\nTemporary password: ${d.temporaryPassword}`);
    }
    setAdminModal(false); fetchAdmins(); fetchHospitals(); fetchAuditLogs(1, auditSearch);
  };

  const toggleAdminActivation = async (a) => {
    const newState = !a.is_active;
    if (!window.confirm(`${newState ? "Activate" : "Deactivate"} admin "${a.username}"?`)) return;
    const res = await fetch(`${API}/admins/${a.id}/toggle-activation`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify(withActor({ is_active: newState })),
    });
    if (!res.ok) { const d = await res.json(); alert(d.error || "Failed"); return; }
    fetchAdmins(); fetchAuditLogs(1, auditSearch);
  };

  const deleteAdmin = async (id) => {
    if (!window.confirm("Delete admin?")) return;
    await fetch(`${API}/admins/${id}`, { method: "DELETE", headers: { "Content-Type": "application/json" }, body: JSON.stringify(withActor({})) });
    fetchAdmins(); fetchHospitals();
  };

  // ── Device actions ─────────────────────────────────────────────────────
  const openAddDev  = () => { setEditingDevice(null); setDeviceForm({ uid: "", hospital_id: "", status: "offline" }); setDeviceModal(true); };
  const openEditDev = (d) => { setEditingDevice(d); const h = hospitals.find((x) => x.name === d.hospital_name); setDeviceForm({ uid: d.uid, hospital_id: h?.id ?? "", status: d.status }); setDeviceModal(true); };

  const submitDev = async (e) => {
    e.preventDefault();
    const body = withActor({ ...deviceForm, hospital_id: deviceForm.hospital_id ? Number(deviceForm.hospital_id) : null });
    const res  = await fetch(editingDevice ? `${API}/devices/${editingDevice.id}` : `${API}/devices`, { method: editingDevice ? "PUT" : "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
    if (!res.ok) { const d = await res.json(); alert(d.error || "Failed"); return; }
    setDeviceModal(false); fetchDevices(devicePage, deviceSearch); fetchHospitals(); fetchAuditLogs(1, auditSearch);
  };

  const deleteDev = async (id) => {
    if (!window.confirm("Delete device?")) return;
    await fetch(`${API}/devices/${id}`, { method: "DELETE", headers: { "Content-Type": "application/json" }, body: JSON.stringify(withActor({})) });
    fetchDevices(devicePage, deviceSearch); fetchHospitals();
  };

  // ── Contact actions ────────────────────────────────────────────────────
  const updateContactStatus = async (id, status) => {
    await fetch(`${API}/contact/${id}/status`, {
      method: "PATCH", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status }),
    });
    fetchContacts(contactPage, contactSearch, contactFilter);
    if (viewContact && viewContact.id === id) setViewContact((c) => ({ ...c, status }));
  };

  const deleteContact = async (id) => {
    if (!window.confirm("Delete this message?")) return;
    await fetch(`${API}/contact/${id}`, { method: "DELETE" });
    setViewContact(null);
    fetchContacts(contactPage, contactSearch, contactFilter);
  };

  const openContact = (c) => {
    setViewContact(c);
    if (c.status === "unread") updateContactStatus(c.id, "read");
  };

  // count unread for badge
  const unreadCount = contacts.filter((c) => c.status === "unread").length;

  // ── Derived ────────────────────────────────────────────────────────────
  const filtHosp           = hospitals.filter((h) => h.name.toLowerCase().includes(hospSearch.toLowerCase()));
  const filtAdmins         = admins.filter((a) => a.username.toLowerCase().includes(adminSearch.toLowerCase()));
  const notActivatedAdmins = admins.filter((a) => !a.is_active).length;
  const getAdminName       = (id) => { if (!id) return "—"; const a = admins.find((x) => x.id === id); return a ? a.username : "—"; };
  const availableAdmins    = admins.filter((a) => a.role === "admin" && (!a.hospital_id || (editingHospital && a.id === editingHospital.admin_id)));
  const totalAuditPages    = Math.ceil(auditTotal / auditPerPage);
  const totalDevicePages   = Math.ceil(deviceTotal / devicesPerPage);
  const totalContactPages  = Math.ceil(contactTotal / contactsPerPage);

  // ── Pager ──────────────────────────────────────────────────────────────
  const Pager = ({ current, total, onPage }) =>
    total <= 1 ? null : (
      <div style={{ display: "flex", justifyContent: "center", gap: ".5rem", marginTop: "1.5rem", flexWrap: "wrap" }}>
        {Array.from({ length: Math.min(total, 10) }, (_, i) => (
          <button key={i + 1} onClick={() => onPage(i + 1)}
            style={{ width: 36, height: 36, borderRadius: "50%", border: "none", cursor: "pointer", fontWeight: 700, fontSize: ".875rem", background: current === i + 1 ? "#3b82f6" : "#f3f4f6", color: current === i + 1 ? "#fff" : "#6b7280", fontFamily: "inherit" }}>
            {i + 1}
          </button>
        ))}
        {total > 10 && <span style={{ color: "#9ca3af", padding: "0 .25rem", lineHeight: "36px" }}>…</span>}
        {total > 10 && (
          <button onClick={() => onPage(total)}
            style={{ width: 36, height: 36, borderRadius: "50%", border: "none", cursor: "pointer", fontWeight: 700, fontSize: ".875rem", background: current === total ? "#3b82f6" : "#f3f4f6", color: current === total ? "#fff" : "#6b7280", fontFamily: "inherit" }}>
            {total}
          </button>
        )}
      </div>
    );

  // ── Audit Detail Modal ─────────────────────────────────────────────────
  const AuditDetailModal = ({ log, onClose }) => {
    if (!log) return null;
    const { verb, detail } = parseAction(log.action);
    const as = actionStyle(log.action);
    return (
      <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.5)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 60, padding: "1rem" }}>
        <div className="card modal-box" style={{ width: "100%", maxWidth: 440, padding: "2rem" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.5rem" }}>
            <div style={{ display: "flex", alignItems: "center", gap: ".75rem" }}>
              <div style={{ width: 40, height: 40, borderRadius: "12px", background: as.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <span className="ms" style={{ color: as.c, fontSize: "1.125rem" }}>history</span>
              </div>
              <div>
                <p style={{ fontWeight: 700, fontSize: "1rem", color: "#111827", margin: 0 }}>{verbLabel(verb)}</p>
                <p style={{ fontSize: ".75rem", color: "#9ca3af", margin: 0 }}>Action detail</p>
              </div>
            </div>
            <button onClick={onClose} style={{ width: 32, height: 32, borderRadius: "50%", background: "#f3f4f6", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#6b7280" }}>
              <span className="ms" style={{ fontSize: "1rem" }}>close</span>
            </button>
          </div>
          {detail && (
            <div style={{ background: as.bg, border: `1.5px solid ${as.c}22`, borderRadius: "1rem", padding: "1rem 1.25rem", marginBottom: "1rem" }}>
              <p style={{ fontSize: ".7rem", fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: ".05em", margin: "0 0 .375rem" }}>Subject</p>
              <p style={{ fontWeight: 700, fontSize: "1.0625rem", color: as.c, margin: 0 }}>{detail}</p>
            </div>
          )}
          {[["Performed by", log.username || "—", "person"], ["Hospital", log.hospital_name || "—", "local_hospital"], ["When", fmtDate(log.created_at), "schedule"], ["Time ago", timeAgo(log.created_at), "access_time"], ["Raw action", log.action, "code"]].map(([label, value, icon]) => (
            <div key={label} style={{ display: "flex", alignItems: "center", gap: ".75rem", padding: ".625rem 0", borderBottom: "1px solid #f3f4f6" }}>
              <span className="ms" style={{ fontSize: ".9rem", color: "#9ca3af", flexShrink: 0 }}>{icon}</span>
              <p style={{ fontSize: ".7rem", color: "#9ca3af", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".04em", margin: 0, width: 90, flexShrink: 0 }}>{label}</p>
              <p style={{ fontWeight: 600, color: "#111827", margin: 0, fontSize: ".875rem", wordBreak: "break-word" }}>{value}</p>
            </div>
          ))}
          <button onClick={onClose} className="bgry" style={{ width: "100%", marginTop: "1.25rem" }}>Close</button>
        </div>
      </div>
    );
  };

  // ── Contact Detail Modal ───────────────────────────────────────────────
  const ContactDetailModal = ({ contact, onClose }) => {
    if (!contact) return null;
    const ss = subjectStyle(contact.subject);
    const st = statusStyle(contact.status);
    return (
      <div className="modal-overlay" style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.5)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 60, padding: "1rem" }}>
        <div className="card modal-box" style={{ width: "100%", maxWidth: 520, padding: "2rem" }}>
          {/* Header */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "1.5rem" }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: ".625rem", marginBottom: ".375rem" }}>
                <span className="badge" style={{ background: ss.bg, color: ss.c }}>{contact.subject}</span>
                <span className="badge" style={{ background: st.bg, color: st.c }}>{contact.status}</span>
              </div>
              <h2 style={{ fontWeight: 700, fontSize: "1.125rem", color: "#111827", margin: 0 }}>{contact.name}</h2>
              <p style={{ fontSize: ".8125rem", color: "#9ca3af", margin: ".125rem 0 0" }}>{contact.email}</p>
            </div>
            <button onClick={onClose} style={{ width: 32, height: 32, borderRadius: "50%", background: "#f3f4f6", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#6b7280", flexShrink: 0 }}>
              <span className="ms" style={{ fontSize: "1rem" }}>close</span>
            </button>
          </div>

          {/* Message */}
          <div style={{ background: "#f9fafb", borderRadius: "1rem", padding: "1.25rem", marginBottom: "1.25rem", border: "1px solid #f3f4f6" }}>
            <p style={{ fontSize: ".7rem", fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: ".05em", margin: "0 0 .625rem" }}>Message</p>
            <p style={{ color: "#374151", fontSize: ".9375rem", lineHeight: 1.7, margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{contact.message}</p>
          </div>

          <p style={{ fontSize: ".75rem", color: "#9ca3af", margin: "0 0 1.25rem", display: "flex", alignItems: "center", gap: ".375rem" }}>
            <span className="ms" style={{ fontSize: ".875rem" }}>schedule</span>
            {fmtDate(contact.created_at)} · {timeAgo(contact.created_at)}
          </p>

          {/* Actions */}
          <div style={{ display: "flex", gap: ".625rem", flexWrap: "wrap" }}>
            {contact.status !== "read" && (
              <button onClick={() => updateContactStatus(contact.id, "read")} className="pill" style={{ background: "#f0fdf4", color: "#15803d" }}>
                <span className="ms" style={{ fontSize: ".875rem" }}>mark_email_read</span>Mark read
              </button>
            )}
            {contact.status !== "archived" && (
              <button onClick={() => updateContactStatus(contact.id, "archived")} className="pill" style={{ background: "#f3f4f6", color: "#4b5563" }}>
                <span className="ms" style={{ fontSize: ".875rem" }}>archive</span>Archive
              </button>
            )}
            {contact.status === "archived" && (
              <button onClick={() => updateContactStatus(contact.id, "unread")} className="pill" style={{ background: "#eff6ff", color: "#1d4ed8" }}>
                <span className="ms" style={{ fontSize: ".875rem" }}>mark_email_unread</span>Mark unread
              </button>
            )}
            <button onClick={() => deleteContact(contact.id)} className="pill" style={{ background: "#fef2f2", color: "#b91c1c", marginLeft: "auto" }}>
              <span className="ms" style={{ fontSize: ".875rem" }}>delete</span>Delete
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ══════════════════════════════════════════════════════════════════════
  return (
    <div style={{ minHeight: "100vh", background: "#f3f4f6", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
        @import url('https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:opsz,wght,FILL,GRAD@24,400,0,0&display=swap');
        .ms { font-family:'Material Symbols Outlined'; font-style:normal; font-weight:normal; line-height:1; display:inline-block; white-space:nowrap; direction:ltr; -webkit-font-smoothing:antialiased; }
        ::-webkit-scrollbar { width:5px; height:5px } ::-webkit-scrollbar-track { background:transparent } ::-webkit-scrollbar-thumb { background:#cbd5e1; border-radius:20px }
        .card { background:#fff; border-radius:1.5rem; box-shadow:0 4px 20px -2px rgba(0,0,0,.06); }
        .pill { border:none; cursor:pointer; font-family:inherit; font-size:.8125rem; font-weight:700; border-radius:9999px; padding:.45rem 1rem; display:inline-flex; align-items:center; gap:.375rem; transition:all .15s; white-space:nowrap; }
        .inp { border:1.5px solid #e5e7eb; border-radius:.875rem; padding:.75rem 1rem; font-size:.875rem; outline:none; width:100%; box-sizing:border-box; font-family:inherit; transition:border-color .15s; background:#fff; }
        .inp:focus { border-color:#93c5fd; box-shadow:0 0 0 3px rgba(59,130,246,.1); }
        .bblu { background:#3b82f6; color:#fff; border:none; font-family:inherit; font-weight:700; border-radius:.875rem; padding:.75rem 1.5rem; cursor:pointer; font-size:.9375rem; display:inline-flex; align-items:center; gap:.5rem; transition:background .15s; }
        .bblu:hover { background:#2563eb; }
        .bgry { background:#f3f4f6; color:#374151; border:none; font-family:inherit; font-weight:600; border-radius:.875rem; padding:.75rem 1.5rem; cursor:pointer; font-size:.9375rem; transition:background .15s; }
        .bgry:hover { background:#e5e7eb; }
        .nav-item { display:flex; align-items:center; gap:.75rem; padding:.75rem 1.25rem; border-radius:1rem; cursor:pointer; font-size:.875rem; font-weight:600; color:#6b7280; transition:all .15s; border:none; background:none; font-family:inherit; width:100%; text-align:left; }
        .nav-item:hover { background:#f9fafb; color:#111827; }
        .nav-item.active { background:#eff6ff; color:#2563eb; }
        .trow { transition: background .1s; }
        .trow:hover { background:#f9fafb; }
        .action-btn { border:none; cursor:pointer; font-family:inherit; font-size:.65rem; font-weight:700; border-radius:9999px; padding:3px 10px; display:inline-flex; align-items:center; gap:.25rem; transition:all .15s; white-space:nowrap; }
        .action-btn:hover { filter: brightness(0.92); transform: scale(0.98); }
        .hovcard { transition:box-shadow .2s; } .hovcard:hover { box-shadow:0 8px 28px -4px rgba(0,0,0,.12); }
        .sect-title { font-size:1.625rem; font-weight:800; color:#111827; letter-spacing:-.02em; margin:0 0 .25rem; }
        .sect-sub { font-size:.875rem; color:#6b7280; margin:0 0 1.75rem; }
        .badge { display:inline-flex; align-items:center; gap:.3rem; font-size:.7rem; font-weight:700; padding:2px 9px; border-radius:9999px; }
        @keyframes fadeIn { from { opacity:0; transform:translateY(6px) } to { opacity:1; transform:translateY(0) } }
        @keyframes pulse { 0%,100%{opacity:1}50%{opacity:.4} }
        @keyframes spin { to { transform:rotate(360deg) } }
        .fade-in { animation:fadeIn .25s ease both }
        .pls { animation:pulse 2s infinite }
        select, textarea, input { font-family:'Plus Jakarta Sans', sans-serif; }
        .toggle-btn { border:none; cursor:pointer; font-family:inherit; font-size:.7rem; font-weight:700; border-radius:9999px; padding:.3rem .75rem; display:inline-flex; align-items:center; gap:.3rem; transition:all .15s; }

        .modal-box { max-height: 90dvh; overflow-y: auto; -webkit-overflow-scrolling: touch; }

        .sidebar { width:256px; flex-shrink:0; background:#fff; border-right:1px solid #f0f0f0; display:flex; flex-direction:column; padding:1.5rem 1rem; position:sticky; top:0; height:100vh; overflow:hidden; }
        .desktop-header { display:flex; }
        .mobile-header { display:none; }
        .bottom-nav { display:none; }
        .main-pad { padding:2rem; max-width:1200px; margin:0 auto; }
        .audit-scroll { overflow-x:auto; -webkit-overflow-scrolling:touch; }
        .audit-row { display:grid; grid-template-columns:100px 1fr 1fr 1fr 110px; gap:1rem; min-width:580px; }

        /* Contact card grid */
        .contact-card-unread { border-left: 3px solid #3b82f6; }

        @media (max-width: 768px) {
          .sidebar { display:none !important; }
          .desktop-header { display:none !important; }
          .mobile-header { display:flex !important; }
          .bottom-nav { display:block; position:fixed; bottom:0; left:0; right:0; background:#fff; border-top:1px solid #f0f0f0; z-index:50; padding-bottom:env(safe-area-inset-bottom); }
          .main-pad { padding:1rem; padding-bottom:calc(64px + env(safe-area-inset-bottom) + 1rem); }
          .modal-overlay { align-items:flex-end !important; padding:0 !important; }
          .modal-box { border-radius:1.5rem 1.5rem 0 0 !important; max-height:92dvh; }
          .sect-title { font-size:1.2rem; }
          .sect-sub { font-size:.78rem; margin-bottom:1.1rem; }
          .stat-grid { grid-template-columns:repeat(2,1fr) !important; gap:.625rem !important; }
          .cards-grid { grid-template-columns:1fr !important; }
          .hosp-mini-grid { grid-template-columns:1fr !important; }
          .contact-filter-row { flex-wrap:wrap; }
        }
      `}</style>

      <div style={{ display: "flex", minHeight: "100vh" }}>

        {/* ══ SIDEBAR ══ */}
        <aside className="sidebar">
          <div style={{ display: "flex", alignItems: "center", gap: ".75rem", padding: "0 .25rem", marginBottom: "2rem" }}>
            <div style={{ width: 38, height: 38, borderRadius: "12px", background: "#3b82f6", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <span className="ms" style={{ color: "#fff", fontSize: "1.125rem" }}>hub</span>
            </div>
            <div>
              <p style={{ fontWeight: 800, fontSize: "1rem", color: "#111827", margin: 0, letterSpacing: "-.02em" }}>CareTech</p>
              <p style={{ fontSize: ".65rem", color: "#9ca3af", margin: 0, textTransform: "uppercase", letterSpacing: ".06em" }}>Super Admin</p>
            </div>
          </div>

          <div style={{ background: "#f9fafb", borderRadius: "1rem", padding: ".875rem 1rem", marginBottom: "1.5rem", display: "flex", alignItems: "center", gap: ".75rem" }}>
            <div style={{ width: 36, height: 36, borderRadius: "50%", background: "#3b82f6", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: ".9375rem", flexShrink: 0 }}>
              {(actorName || "S").charAt(0).toUpperCase()}
            </div>
            <div style={{ minWidth: 0 }}>
              <p style={{ fontWeight: 700, fontSize: ".875rem", color: "#111827", margin: 0 }}>{actorName}</p>
              <p style={{ fontSize: ".7rem", color: "#9ca3af", margin: 0 }}>Super Administrator</p>
            </div>
          </div>

          <nav style={{ flex: 1, display: "flex", flexDirection: "column", gap: ".25rem" }}>
            {NAV.map((n) => (
              <button key={n.id} onClick={() => setPage(n.id)} className={`nav-item ${page === n.id ? "active" : ""}`}>
                <span className="ms" style={{ fontSize: "1.125rem" }}>{n.icon}</span>
                {n.label}
                {n.id === "admins" && notActivatedAdmins > 0 && (
                  <span style={{ marginLeft: "auto", background: "#f59e0b", color: "#fff", fontSize: ".6rem", fontWeight: 700, padding: "2px 7px", borderRadius: "9999px" }}>{notActivatedAdmins}</span>
                )}
                {n.id === "contacts" && unreadCount > 0 && (
                  <span style={{ marginLeft: "auto", background: "#3b82f6", color: "#fff", fontSize: ".6rem", fontWeight: 700, padding: "2px 7px", borderRadius: "9999px" }}>{unreadCount}</span>
                )}
              </button>
            ))}
          </nav>

          <button onClick={handleLogout} className="nav-item" style={{ color: "#ef4444", marginTop: "1rem" }}>
            <span className="ms" style={{ fontSize: "1.125rem" }}>logout</span>Log Out
          </button>
        </aside>

        {/* ══ MAIN ══ */}
        <main style={{ flex: 1, minWidth: 0, overflowY: "auto" }}>

          {/* Desktop header */}
          <header className="desktop-header" style={{ background: "#fff", borderBottom: "1px solid #f0f0f0", padding: "1rem 2rem", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 40 }}>
            <h1 style={{ fontWeight: 800, fontSize: "1.125rem", color: "#111827", margin: 0, letterSpacing: "-.02em" }}>
              {NAV.find((n) => n.id === page)?.label || "Dashboard"}
            </h1>
            <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
              <div style={{ display: "flex", alignItems: "center", gap: ".5rem" }}>
                <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#22c55e" }} className="pls" />
                <span style={{ fontSize: ".75rem", color: "#6b7280" }}>Live</span>
              </div>
              {notActivatedAdmins > 0 && (
                <button onClick={() => setPage("admins")} style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: "9999px", padding: ".35rem .9rem", color: "#b45309", fontSize: ".75rem", fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: ".4rem" }}>
                  <span className="ms" style={{ fontSize: ".9rem" }}>person_off</span>{notActivatedAdmins} not activated
                </button>
              )}
            </div>
          </header>

          {/* Mobile header */}
          <header className="mobile-header" style={{ background: "#fff", borderBottom: "1px solid #f0f0f0", padding: ".75rem 1rem", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 40 }}>
            <div style={{ display: "flex", alignItems: "center", gap: ".625rem" }}>
              <div style={{ width: 32, height: 32, borderRadius: "10px", background: "#3b82f6", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <span className="ms" style={{ color: "#fff", fontSize: "1rem" }}>hub</span>
              </div>
              <div>
                <p style={{ fontWeight: 800, fontSize: ".9rem", color: "#111827", margin: 0 }}>CareTech</p>
                <p style={{ fontSize: ".6rem", color: "#9ca3af", margin: 0, textTransform: "uppercase", letterSpacing: ".06em" }}>Super Admin</p>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: ".625rem" }}>
              <div style={{ display: "flex", alignItems: "center", gap: ".375rem" }}>
                <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#22c55e" }} className="pls" />
                <span style={{ fontSize: ".7rem", color: "#6b7280" }}>Live</span>
              </div>
              <button onClick={handleLogout} style={{ width: 32, height: 32, borderRadius: "10px", background: "#fef2f2", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#ef4444" }}>
                <span className="ms" style={{ fontSize: "1rem" }}>logout</span>
              </button>
            </div>
          </header>

          <div className="main-pad fade-in">

            {/* ════════ OVERVIEW ════════ */}
            {page === "overview" && (
              <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
                <div>
                  <h2 className="sect-title">System Overview</h2>
                  <p className="sect-sub">Global CareTech platform summary.</p>
                </div>
                <div className="stat-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: "1rem" }}>
                  {[
                    { label: "Hospitals",        val: hospitals.length,                            icon: "local_hospital",       bg: "#eff6ff", c: "#2563eb" },
                    { label: "Active Hospitals",  val: hospitals.filter((h) => h.is_active).length, icon: "check_circle",         bg: "#f0fdf4", c: "#16a34a" },
                    { label: "Admins",            val: admins.length,                               icon: "admin_panel_settings", bg: "#f5f3ff", c: "#7c3aed" },
                    { label: "Not Activated",     val: notActivatedAdmins,                          icon: "person_off",           bg: "#fffbeb", c: "#d97706" },
                    { label: "Total Devices",     val: deviceTotal,                                 icon: "devices",              bg: "#fef3c7", c: "#d97706" },
                    { label: "Contact Messages",  val: contactTotal,                                icon: "mail",                 bg: "#eff6ff", c: "#2563eb" },
                    { label: "Audit Events",      val: auditTotal,                                  icon: "assignment",           bg: "#fef2f2", c: "#dc2626" },
                  ].map((s) => (
                    <div key={s.label} className="card hovcard" style={{ padding: "1.25rem" }}>
                      <div style={{ width: 40, height: 40, borderRadius: "12px", background: s.bg, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: ".875rem" }}>
                        <span className="ms" style={{ color: s.c, fontSize: "1.125rem" }}>{s.icon}</span>
                      </div>
                      <p style={{ fontSize: "1.875rem", fontWeight: 800, color: "#111827", margin: "0 0 .125rem" }}>{s.val}</p>
                      <p style={{ fontSize: ".75rem", color: "#9ca3af", fontWeight: 600, margin: 0, textTransform: "uppercase", letterSpacing: ".04em" }}>{s.label}</p>
                    </div>
                  ))}
                </div>

                <div className="card" style={{ padding: "1.5rem" }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem" }}>
                    <h3 style={{ fontWeight: 700, fontSize: "1rem", color: "#111827", margin: 0 }}>Hospitals</h3>
                    <button onClick={() => setPage("hospitals")} style={{ background: "none", border: "none", color: "#3b82f6", fontSize: ".8125rem", fontWeight: 600, cursor: "pointer" }}>See all →</button>
                  </div>
                  <div className="hosp-mini-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(220px,1fr))", gap: "1rem" }}>
                    {hospitals.slice(0, 6).map((h) => (
                      <div key={h.id} className="hovcard" style={{ borderRadius: "1rem", padding: "1rem", background: "#f9fafb", border: "1.5px solid #f3f4f6", cursor: "pointer" }} onClick={() => { setViewHospModal(h); setPage("hospitals"); }}>
                        <div style={{ display: "flex", alignItems: "center", gap: ".75rem" }}>
                          <div style={{ width: 36, height: 36, borderRadius: "10px", background: h.is_active ? "#eff6ff" : "#f3f4f6", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                            <span className="ms" style={{ color: h.is_active ? "#2563eb" : "#9ca3af", fontSize: "1rem" }}>local_hospital</span>
                          </div>
                          <div style={{ minWidth: 0 }}>
                            <p style={{ fontWeight: 700, fontSize: ".875rem", color: "#111827", margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{h.name}</p>
                            <span className="badge" style={{ background: h.is_active ? "#f0fdf4" : "#fef2f2", color: h.is_active ? "#166534" : "#991b1b", marginTop: ".125rem" }}>
                              <span style={{ width: 5, height: 5, borderRadius: "50%", background: h.is_active ? "#22c55e" : "#ef4444" }} />
                              {h.is_active ? "Activated" : "Not Activated"}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* ════════ HOSPITALS ════════ */}
            {page === "hospitals" && (
              <div>
                <h2 className="sect-title">Hospitals</h2>
                <p className="sect-sub">Manage all medical facilities and their activation status.</p>
                <div style={{ display: "flex", gap: ".75rem", marginBottom: "1.25rem", flexWrap: "wrap" }}>
                  <div style={{ position: "relative", flex: 1, maxWidth: 340 }}>
                    <span className="ms" style={{ position: "absolute", left: ".875rem", top: "50%", transform: "translateY(-50%)", color: "#9ca3af", fontSize: "1.125rem" }}>search</span>
                    <input className="inp" style={{ paddingLeft: "2.75rem" }} placeholder="Search hospitals…" value={hospSearch} onChange={(e) => setHospSearch(e.target.value)} />
                  </div>
                  <button onClick={openAddHosp} className="bblu" style={{ boxShadow: "0 4px 14px rgba(59,130,246,.3)" }}>
                    <span className="ms" style={{ fontSize: "1.125rem" }}>add_circle</span>Add Hospital
                  </button>
                </div>
                <div className="cards-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(280px,1fr))", gap: "1rem" }}>
                  {filtHosp.length === 0 && <p style={{ color: "#9ca3af" }}>No hospitals found.</p>}
                  {filtHosp.map((h) => (
                    <div key={h.id} className="card hovcard" style={{ padding: "1.25rem" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: ".875rem", marginBottom: "1rem" }}>
                        <div style={{ width: 44, height: 44, borderRadius: "14px", background: h.is_active ? "#eff6ff" : "#f3f4f6", color: h.is_active ? "#2563eb" : "#9ca3af", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          <span className="ms" style={{ fontSize: "1.25rem" }}>local_hospital</span>
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p style={{ fontWeight: 700, color: "#111827", margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{h.name}</p>
                          <span className="badge" style={{ background: h.is_active ? "#f0fdf4" : "#fef2f2", color: h.is_active ? "#166534" : "#991b1b", marginTop: ".125rem" }}>
                            <span style={{ width: 5, height: 5, borderRadius: "50%", background: h.is_active ? "#22c55e" : "#ef4444" }} />
                            {h.is_active ? "Activated" : "Not Activated"}
                          </span>
                        </div>
                      </div>
                      <div style={{ fontSize: ".8125rem", color: "#6b7280", marginBottom: ".375rem", display: "flex", alignItems: "center", gap: ".375rem" }}>
                        <span className="ms" style={{ fontSize: ".875rem" }}>location_on</span>{h.address || "—"}
                      </div>
                      <div style={{ fontSize: ".8125rem", color: "#6b7280", marginBottom: ".375rem", display: "flex", alignItems: "center", gap: ".375rem" }}>
                        <span className="ms" style={{ fontSize: ".875rem" }}>devices</span>{h.device_count} devices
                      </div>
                      <div style={{ fontSize: ".8125rem", color: "#6b7280", marginBottom: ".875rem", display: "flex", alignItems: "center", gap: ".375rem" }}>
                        <span className="ms" style={{ fontSize: ".875rem" }}>admin_panel_settings</span>{getAdminName(h.admin_id)}
                      </div>
                      <div style={{ display: "flex", gap: ".5rem", flexWrap: "wrap" }}>
                        <button onClick={() => setViewHospModal(h)} className="pill" style={{ flex: 1, background: "#eff6ff", color: "#2563eb", justifyContent: "center" }}>
                          <span className="ms" style={{ fontSize: ".9rem" }}>visibility</span>View
                        </button>
                        <button onClick={() => toggleHospitalActivation(h)} className="toggle-btn" style={{ background: h.is_active ? "#fef2f2" : "#f0fdf4", color: h.is_active ? "#991b1b" : "#166534" }}>
                          <span className="ms" style={{ fontSize: ".875rem" }}>{h.is_active ? "block" : "check_circle"}</span>
                          {h.is_active ? "Deactivate" : "Activate"}
                        </button>
                        <button onClick={() => openEditHosp(h)} style={{ width: 36, height: 36, borderRadius: "10px", background: "#f9fafb", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#6b7280" }}>
                          <span className="ms" style={{ fontSize: ".875rem" }}>edit</span>
                        </button>
                        <button onClick={() => deleteHosp(h.id)} style={{ width: 36, height: 36, borderRadius: "10px", background: "#fef2f2", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#ef4444" }}>
                          <span className="ms" style={{ fontSize: ".875rem" }}>delete</span>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ════════ ADMINS ════════ */}
            {page === "admins" && (
              <div>
                <h2 className="sect-title">Administrators</h2>
                <p className="sect-sub">Manage admin accounts.</p>
                <div style={{ display: "flex", gap: ".75rem", marginBottom: "1.25rem", flexWrap: "wrap" }}>
                  <div style={{ position: "relative", flex: 1, maxWidth: 340 }}>
                    <span className="ms" style={{ position: "absolute", left: ".875rem", top: "50%", transform: "translateY(-50%)", color: "#9ca3af", fontSize: "1.125rem" }}>search</span>
                    <input className="inp" style={{ paddingLeft: "2.75rem" }} placeholder="Search admins…" value={adminSearch} onChange={(e) => setAdminSearch(e.target.value)} />
                  </div>
                  <button onClick={openAddAdmin} className="bblu" style={{ boxShadow: "0 4px 14px rgba(59,130,246,.3)" }}>
                    <span className="ms" style={{ fontSize: "1.125rem" }}>add_circle</span>Add Admin
                  </button>
                </div>
                <div className="cards-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(280px,1fr))", gap: "1rem" }}>
                  {filtAdmins.length === 0 && <p style={{ color: "#9ca3af" }}>No admins found.</p>}
                  {filtAdmins.map((a) => (
                    <div key={a.id} className="card hovcard" style={{ padding: "1.25rem" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: ".875rem", marginBottom: "1rem" }}>
                        <div style={{ width: 44, height: 44, borderRadius: "14px", background: a.is_active ? "#f5f3ff" : "#f3f4f6", color: a.is_active ? "#7c3aed" : "#9ca3af", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          <span className="ms" style={{ fontSize: "1.25rem" }}>admin_panel_settings</span>
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p style={{ fontWeight: 700, color: "#111827", margin: 0 }}>{a.username}</p>
                          <p style={{ fontSize: ".75rem", color: "#9ca3af", margin: 0 }}>{a.email}</p>
                        </div>
                        <span className="badge" style={{ background: a.is_active ? "#f0fdf4" : "#fef2f2", color: a.is_active ? "#166534" : "#991b1b" }}>
                          {a.is_active ? "Activated" : "Not Activated"}
                        </span>
                      </div>
                      <div style={{ fontSize: ".8125rem", color: "#6b7280", marginBottom: ".875rem", display: "flex", alignItems: "center", gap: ".375rem" }}>
                        <span className="ms" style={{ fontSize: ".875rem" }}>local_hospital</span>{a.hospital_name || "—"}
                        {a.hospital_is_active === 0 && <span className="badge" style={{ background: "#fef2f2", color: "#991b1b", marginLeft: ".25rem" }}>Hospital Deactivated</span>}
                      </div>
                      <div style={{ display: "flex", gap: ".5rem", flexWrap: "wrap" }}>
                        <button onClick={() => setViewAdminModal(a)} className="pill" style={{ flex: 1, background: "#f5f3ff", color: "#7c3aed", justifyContent: "center" }}>
                          <span className="ms" style={{ fontSize: ".9rem" }}>visibility</span>View
                        </button>
                        <button onClick={() => toggleAdminActivation(a)} className="toggle-btn" style={{ background: a.is_active ? "#fef2f2" : "#f0fdf4", color: a.is_active ? "#991b1b" : "#166534" }}>
                          <span className="ms" style={{ fontSize: ".875rem" }}>{a.is_active ? "block" : "check_circle"}</span>
                          {a.is_active ? "Deactivate" : "Activate"}
                        </button>
                        <button onClick={() => openEditAdmin(a)} style={{ width: 36, height: 36, borderRadius: "10px", background: "#f9fafb", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#6b7280" }}>
                          <span className="ms" style={{ fontSize: ".875rem" }}>edit</span>
                        </button>
                        <button onClick={() => deleteAdmin(a.id)} style={{ width: 36, height: 36, borderRadius: "10px", background: "#fef2f2", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#ef4444" }}>
                          <span className="ms" style={{ fontSize: ".875rem" }}>delete</span>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ════════ DEVICES ════════ */}
            {page === "devices" && (
              <div>
                <h2 className="sect-title">Medical Devices</h2>
                <p className="sect-sub">Monitor and manage all connected devices across hospitals.</p>
                <div style={{ display: "flex", gap: ".75rem", marginBottom: "1.25rem", flexWrap: "wrap" }}>
                  <div style={{ position: "relative", flex: 1, maxWidth: 340 }}>
                    <span className="ms" style={{ position: "absolute", left: ".875rem", top: "50%", transform: "translateY(-50%)", color: "#9ca3af", fontSize: "1.125rem" }}>search</span>
                    <input className="inp" style={{ paddingLeft: "2.75rem" }} placeholder="Search devices…" value={deviceSearch} onChange={(e) => { setDeviceSearch(e.target.value); setDevicePage(1); }} />
                  </div>
                  <button onClick={openAddDev} className="bblu" style={{ boxShadow: "0 4px 14px rgba(59,130,246,.3)" }}>
                    <span className="ms" style={{ fontSize: "1.125rem" }}>add_circle</span>Add Device
                  </button>
                </div>
                {deviceLoading ? (
                  <div style={{ display: "flex", justifyContent: "center", padding: "4rem" }}>
                    <div style={{ width: 44, height: 44, border: "4px solid #dbeafe", borderTopColor: "#3b82f6", borderRadius: "50%", animation: "spin 1s linear infinite" }} />
                  </div>
                ) : (
                  <div className="cards-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(280px,1fr))", gap: "1rem" }}>
                    {devices.length === 0 && <p style={{ color: "#9ca3af" }}>No devices found.</p>}
                    {devices.map((d) => (
                      <div key={d.id} className="card hovcard" style={{ padding: "1.25rem" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: ".875rem", marginBottom: "1rem" }}>
                          <div style={{ width: 44, height: 44, borderRadius: "14px", background: d.status === "connected" ? "#f0fdf4" : "#f3f4f6", color: d.status === "connected" ? "#16a34a" : "#9ca3af", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                            <span className="ms">devices</span>
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <p style={{ fontWeight: 700, color: "#111827", margin: 0, fontFamily: "monospace", fontSize: ".875rem" }}>{d.uid}</p>
                            <span className="badge" style={{ background: d.status === "connected" ? "#f0fdf4" : "#f3f4f6", color: d.status === "connected" ? "#166534" : "#6b7280", marginTop: ".125rem" }}>
                              <span style={{ width: 5, height: 5, borderRadius: "50%", background: d.status === "connected" ? "#22c55e" : "#9ca3af" }} />
                              {d.status}
                            </span>
                          </div>
                        </div>
                        <div style={{ fontSize: ".8125rem", color: "#6b7280", marginBottom: ".375rem", display: "flex", alignItems: "center", gap: ".375rem" }}>
                          <span className="ms" style={{ fontSize: ".875rem" }}>local_hospital</span>{d.hospital_name || <em style={{ color: "#9ca3af" }}>Unassigned</em>}
                        </div>
                        <div style={{ fontSize: ".8125rem", color: "#6b7280", marginBottom: ".875rem", display: "flex", alignItems: "center", gap: ".375rem" }}>
                          <span className="ms" style={{ fontSize: ".875rem" }}>schedule</span>{fmtDate(d.last_seen)}
                        </div>
                        <div style={{ display: "flex", gap: ".5rem" }}>
                          <button onClick={() => openEditDev(d)} className="pill" style={{ flex: 1, background: "#eff6ff", color: "#2563eb", justifyContent: "center" }}>
                            <span className="ms" style={{ fontSize: ".9rem" }}>edit</span>Edit
                          </button>
                          <button onClick={() => deleteDev(d.id)} style={{ width: 36, height: 36, borderRadius: "10px", background: "#fef2f2", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#ef4444" }}>
                            <span className="ms" style={{ fontSize: ".875rem" }}>delete</span>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <Pager current={devicePage} total={totalDevicePages} onPage={setDevicePage} />
              </div>
            )}

            {/* ════════ CONTACTS ════════ */}
            {page === "contacts" && (
              <div>
                <h2 className="sect-title">Contact Messages</h2>
                <p className="sect-sub">Messages submitted through the CareTech contact form.</p>

                {/* Toolbar */}
                <div className="contact-filter-row" style={{ display: "flex", gap: ".75rem", marginBottom: "1.25rem", alignItems: "center", flexWrap: "wrap" }}>
                  <div style={{ position: "relative", flex: 1, minWidth: 200, maxWidth: 340 }}>
                    <span className="ms" style={{ position: "absolute", left: ".875rem", top: "50%", transform: "translateY(-50%)", color: "#9ca3af", fontSize: "1.125rem" }}>search</span>
                    <input className="inp" style={{ paddingLeft: "2.75rem" }} placeholder="Search messages…"
                      value={contactSearch}
                      onChange={(e) => { setContactSearch(e.target.value); setContactPage(1); }} />
                  </div>

                  {/* Status filter pills */}
                  <div style={{ display: "flex", gap: ".5rem", flexWrap: "wrap" }}>
                    {[
                      { val: "",         label: "All" },
                      { val: "unread",   label: "Unread" },
                      { val: "read",     label: "Read" },
                      { val: "archived", label: "Archived" },
                    ].map(({ val, label }) => (
                      <button key={val} onClick={() => { setContactFilter(val); setContactPage(1); }}
                        style={{
                          border: "none", cursor: "pointer", fontFamily: "inherit", fontSize: ".8125rem", fontWeight: 700,
                          borderRadius: "9999px", padding: ".4rem 1rem", transition: "all .15s",
                          background: contactFilter === val ? "#3b82f6" : "#f3f4f6",
                          color:      contactFilter === val ? "#fff"    : "#6b7280",
                        }}>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Cards */}
                {contactLoading ? (
                  <div style={{ display: "flex", justifyContent: "center", padding: "4rem" }}>
                    <div style={{ width: 44, height: 44, border: "4px solid #dbeafe", borderTopColor: "#3b82f6", borderRadius: "50%", animation: "spin 1s linear infinite" }} />
                  </div>
                ) : contacts.length === 0 ? (
                  <div className="card" style={{ padding: "3rem", textAlign: "center" }}>
                    <span className="ms" style={{ fontSize: "2.5rem", color: "#d1d5db", display: "block", marginBottom: ".75rem" }}>mail</span>
                    <p style={{ color: "#9ca3af", margin: 0, fontWeight: 600 }}>No messages found.</p>
                  </div>
                ) : (
                  <div className="cards-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(300px,1fr))", gap: "1rem" }}>
                    {contacts.map((c) => {
                      const ss = subjectStyle(c.subject);
                      const st = statusStyle(c.status);
                      return (
                        <div
                          key={c.id}
                          className={`card hovcard${c.status === "unread" ? " contact-card-unread" : ""}`}
                          style={{ padding: "1.25rem", cursor: "pointer", position: "relative" }}
                          onClick={() => openContact(c)}
                        >
                          {/* Unread dot */}
                          {c.status === "unread" && (
                            <div style={{ position: "absolute", top: "1.125rem", right: "1.125rem", width: 8, height: 8, borderRadius: "50%", background: "#3b82f6" }} className="pls" />
                          )}

                          {/* Avatar + name */}
                          <div style={{ display: "flex", alignItems: "center", gap: ".75rem", marginBottom: ".875rem" }}>
                            <div style={{ width: 40, height: 40, borderRadius: "50%", background: "#eff6ff", color: "#2563eb", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: ".9375rem", flexShrink: 0 }}>
                              {c.name.charAt(0).toUpperCase()}
                            </div>
                            <div style={{ minWidth: 0 }}>
                              <p style={{ fontWeight: 700, color: "#111827", margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.name}</p>
                              <p style={{ fontSize: ".75rem", color: "#9ca3af", margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.email}</p>
                            </div>
                          </div>

                          {/* Badges */}
                          <div style={{ display: "flex", gap: ".375rem", marginBottom: ".75rem", flexWrap: "wrap" }}>
                            <span className="badge" style={{ background: ss.bg, color: ss.c }}>{c.subject}</span>
                            <span className="badge" style={{ background: st.bg, color: st.c }}>{c.status}</span>
                          </div>

                          {/* Message preview */}
                          <p style={{ fontSize: ".8125rem", color: "#6b7280", margin: "0 0 .75rem", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", lineHeight: 1.5 }}>
                            {c.message}
                          </p>

                          {/* Footer */}
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                            <span style={{ fontSize: ".75rem", color: "#9ca3af", display: "flex", alignItems: "center", gap: ".25rem" }}>
                              <span className="ms" style={{ fontSize: ".8rem" }}>schedule</span>
                              {timeAgo(c.created_at)}
                            </span>
                            <span style={{ fontSize: ".75rem", color: "#3b82f6", fontWeight: 600 }}>View →</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                <Pager current={contactPage} total={totalContactPages} onPage={setContactPage} />
              </div>
            )}

            {/* ════════ AUDIT TRAIL ════════ */}
            {page === "audit" && (
              <div>
                <h2 className="sect-title">Audit Trail</h2>
                <p className="sect-sub">Complete system activity log. Click any action to see full details.</p>
                <div style={{ position: "relative", maxWidth: 340, marginBottom: "1.25rem" }}>
                  <span className="ms" style={{ position: "absolute", left: ".875rem", top: "50%", transform: "translateY(-50%)", color: "#9ca3af", fontSize: "1.125rem" }}>search</span>
                  <input className="inp" style={{ paddingLeft: "2.75rem" }} placeholder="Search logs…" value={auditSearch} onChange={(e) => { setAuditSearch(e.target.value); setAuditPage(1); }} />
                </div>
                <div className="card" style={{ overflow: "hidden" }}>
                  <div className="audit-scroll">
                    <div className="audit-row" style={{ padding: ".75rem 1.25rem", background: "#f9fafb", borderBottom: "1px solid #f3f4f6" }}>
                      {["When", "User", "Hospital", "Action", "Full time"].map((h) => (
                        <span key={h} style={{ fontSize: ".7rem", fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: ".06em" }}>{h}</span>
                      ))}
                    </div>
                    {auditLoading ? (
                      <div style={{ display: "flex", justifyContent: "center", padding: "3rem" }}>
                        <div style={{ width: 36, height: 36, border: "4px solid #dbeafe", borderTopColor: "#3b82f6", borderRadius: "50%", animation: "spin 1s linear infinite" }} />
                      </div>
                    ) : auditLogs.length === 0 ? (
                      <div style={{ padding: "3rem", textAlign: "center", color: "#9ca3af" }}>No logs found.</div>
                    ) : (
                      auditLogs.map((log, i) => {
                        const as = actionStyle(log.action);
                        const { verb, detail } = parseAction(log.action);
                        return (
                          <div key={log.id} className="trow audit-row" style={{ padding: ".875rem 1.25rem", borderBottom: i < auditLogs.length - 1 ? "1px solid #f3f4f6" : "none", alignItems: "center" }}>
                            <div><span style={{ fontSize: ".8rem", fontWeight: 600, color: "#374151" }}>{timeAgo(log.created_at)}</span></div>
                            <div style={{ display: "flex", alignItems: "center", gap: ".5rem", minWidth: 0 }}>
                              <div style={{ width: 28, height: 28, borderRadius: "50%", background: "#eff6ff", color: "#2563eb", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: ".7rem", flexShrink: 0 }}>
                                {(log.username || "?").charAt(0).toUpperCase()}
                              </div>
                              <span style={{ fontWeight: 600, color: "#111827", fontSize: ".875rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{log.username}</span>
                            </div>
                            <span style={{ fontSize: ".875rem", color: "#6b7280", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{log.hospital_name || "—"}</span>
                            <div>
                              <button className="action-btn" onClick={() => setAuditDetailModal(log)}
                                style={{ background: as.bg, color: as.c, maxWidth: "100%" }}
                                title={detail ? `${verbLabel(verb)}: ${detail}` : verbLabel(verb)}>
                                <span className="ms" style={{ fontSize: ".75rem" }}>history</span>
                                <span style={{ overflow: "hidden", textOverflow: "ellipsis", maxWidth: 130 }}>
                                  {verbLabel(verb)}{detail ? `: ${detail}` : ""}
                                </span>
                              </button>
                            </div>
                            <span style={{ fontSize: ".75rem", color: "#9ca3af" }}>{fmtDate(log.created_at)}</span>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
                <Pager current={auditPage} total={totalAuditPages} onPage={setAuditPage} />
              </div>
            )}

          </div>
        </main>
      </div>

      {/* ══ BOTTOM NAV (mobile) ══ */}
      <nav className="bottom-nav">
        <div style={{ display: "flex", height: 60 }}>
          {NAV.map((n) => (
            <button key={n.id} onClick={() => setPage(n.id)}
              style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 2, border: "none", background: "none", cursor: "pointer", fontFamily: "inherit", fontSize: ".58rem", fontWeight: 700, color: page === n.id ? "#2563eb" : "#9ca3af", padding: 0, position: "relative", transition: "color .15s" }}>
              <span className="ms" style={{ fontSize: "1.3rem" }}>{n.icon}</span>
              <span>{n.label}</span>
              {n.id === "admins" && notActivatedAdmins > 0 && (
                <span style={{ position: "absolute", top: 6, right: "calc(50% - 20px)", background: "#f59e0b", color: "#fff", fontSize: ".55rem", fontWeight: 700, padding: "1px 5px", borderRadius: "9999px", lineHeight: 1.4 }}>{notActivatedAdmins}</span>
              )}
              {n.id === "contacts" && unreadCount > 0 && (
                <span style={{ position: "absolute", top: 6, right: "calc(50% - 20px)", background: "#3b82f6", color: "#fff", fontSize: ".55rem", fontWeight: 700, padding: "1px 5px", borderRadius: "9999px", lineHeight: 1.4 }}>{unreadCount}</span>
              )}
            </button>
          ))}
          <button onClick={handleLogout}
            style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 2, border: "none", background: "none", cursor: "pointer", fontFamily: "inherit", fontSize: ".58rem", fontWeight: 700, color: "#ef4444", padding: 0, transition: "color .15s" }}>
            <span className="ms" style={{ fontSize: "1.3rem" }}>logout</span>
            <span>Logout</span>
          </button>
        </div>
      </nav>

      {/* ══ MODALS ══ */}

      <AuditDetailModal log={auditDetailModal} onClose={() => setAuditDetailModal(null)} />
      <ContactDetailModal contact={viewContact} onClose={() => setViewContact(null)} />

      {/* View Hospital */}
      {viewHospModal && (
        <div className="modal-overlay" style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50, padding: "1rem" }}>
          <div className="card modal-box" style={{ width: "100%", maxWidth: 480, padding: "2rem" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.25rem" }}>
              <h2 style={{ fontWeight: 700, fontSize: "1.25rem", color: "#111827", margin: 0 }}>Hospital Details</h2>
              <button onClick={() => setViewHospModal(null)} style={{ width: 32, height: 32, borderRadius: "50%", background: "#f3f4f6", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#6b7280" }}>
                <span className="ms" style={{ fontSize: "1rem" }}>close</span>
              </button>
            </div>
            {[["Name", viewHospModal.name], ["Address", viewHospModal.address || "—"], ["Email", viewHospModal.contact_email || "—"], ["Phone", viewHospModal.contact_phone || "—"], ["Status", viewHospModal.is_active ? "Activated" : "Not Activated"], ["Devices", viewHospModal.device_count], ["Admin", getAdminName(viewHospModal.admin_id)]].map(([l, v]) => (
              <div key={l} style={{ background: "#f9fafb", borderRadius: "1rem", padding: ".875rem 1rem", marginBottom: ".625rem" }}>
                <p style={{ fontSize: ".7rem", color: "#9ca3af", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em", margin: "0 0 .25rem" }}>{l}</p>
                <p style={{ fontWeight: 600, color: "#111827", margin: 0 }}>{String(v)}</p>
              </div>
            ))}
            <button onClick={() => setViewHospModal(null)} className="bgry" style={{ width: "100%", marginTop: ".75rem" }}>Close</button>
          </div>
        </div>
      )}

      {/* Add/Edit Hospital */}
      {hospitalModal && (
        <div className="modal-overlay" style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50, padding: "1rem" }}>
          <div className="card modal-box" style={{ width: "100%", maxWidth: 560, padding: "2rem" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.25rem" }}>
              <h2 style={{ fontWeight: 700, fontSize: "1.25rem", color: "#111827", margin: 0 }}>{editingHospital ? "Edit Hospital" : "Add Hospital"}</h2>
              <button onClick={() => setHospitalModal(false)} style={{ width: 32, height: 32, borderRadius: "50%", background: "#f3f4f6", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#6b7280" }}>
                <span className="ms" style={{ fontSize: "1rem" }}>close</span>
              </button>
            </div>
            <form onSubmit={submitHosp} style={{ display: "flex", flexDirection: "column", gap: ".875rem" }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: ".875rem" }}>
                {[["Name *", "name", "text", true], ["Address", "address", "text", false], ["Email", "contact_email", "email", false], ["Phone", "contact_phone", "text", false]].map(([l, k, t, r]) => (
                  <div key={k}>
                    <label style={{ display: "block", fontSize: ".7rem", fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: ".05em", marginBottom: ".375rem" }}>{l}</label>
                    <input type={t} required={r} className="inp" value={hospitalForm[k]} onChange={(e) => setHospitalForm((p) => ({ ...p, [k]: e.target.value }))} />
                  </div>
                ))}
              </div>
              <div>
                <label style={{ display: "block", fontSize: ".7rem", fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: ".05em", marginBottom: ".375rem" }}>Activation Status</label>
                <select className="inp" value={hospitalForm.is_active ? "true" : "false"} onChange={(e) => setHospitalForm((p) => ({ ...p, is_active: e.target.value === "true" }))}>
                  <option value="true">Activated — Hospital operations allowed</option>
                  <option value="false">Not Activated — All operations blocked</option>
                </select>
              </div>
              <div>
                <label style={{ display: "block", fontSize: ".7rem", fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: ".05em", marginBottom: ".375rem" }}>Assign Admin</label>
                <select className="inp" value={hospitalForm.admin_id} onChange={(e) => setHospitalForm((p) => ({ ...p, admin_id: e.target.value }))}>
                  <option value="">— No Admin —</option>
                  {availableAdmins.map((a) => <option key={a.id} value={a.id}>{a.username} ({a.email}){!a.is_active ? " [Not Activated]" : ""}</option>)}
                </select>
              </div>
              <div style={{ display: "flex", gap: ".75rem", marginTop: ".25rem" }}>
                <button type="submit" className="bblu" style={{ flex: 1, justifyContent: "center" }}>{editingHospital ? "Update" : "Add"}</button>
                <button type="button" onClick={() => setHospitalModal(false)} className="bgry" style={{ flex: 1 }}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Admin */}
      {viewAdminModal && (
        <div className="modal-overlay" style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50, padding: "1rem" }}>
          <div className="card modal-box" style={{ width: "100%", maxWidth: 420, padding: "2rem" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.25rem" }}>
              <h2 style={{ fontWeight: 700, fontSize: "1.25rem", color: "#111827", margin: 0 }}>Admin Details</h2>
              <button onClick={() => setViewAdminModal(null)} style={{ width: 32, height: 32, borderRadius: "50%", background: "#f3f4f6", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#6b7280" }}>
                <span className="ms" style={{ fontSize: "1rem" }}>close</span>
              </button>
            </div>
            {[["Username", viewAdminModal.username], ["Email", viewAdminModal.email], ["Hospital", viewAdminModal.hospital_name || "—"], ["Role", viewAdminModal.role], ["Status", viewAdminModal.is_active ? "Activated" : "Not Activated"]].map(([l, v]) => (
              <div key={l} style={{ background: "#f9fafb", borderRadius: "1rem", padding: ".875rem 1rem", marginBottom: ".625rem" }}>
                <p style={{ fontSize: ".7rem", color: "#9ca3af", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em", margin: "0 0 .25rem" }}>{l}</p>
                <p style={{ fontWeight: 600, color: "#111827", margin: 0 }}>{v}</p>
              </div>
            ))}
            <button onClick={() => setViewAdminModal(null)} className="bgry" style={{ width: "100%", marginTop: ".75rem" }}>Close</button>
          </div>
        </div>
      )}

      {/* Add/Edit Admin */}
      {adminModal && (
        <div className="modal-overlay" style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50, padding: "1rem" }}>
          <div className="card modal-box" style={{ width: "100%", maxWidth: 520, padding: "2rem" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.25rem" }}>
              <h2 style={{ fontWeight: 700, fontSize: "1.25rem", color: "#111827", margin: 0 }}>{editingAdmin ? "Edit Admin" : "Add Admin"}</h2>
              <button onClick={() => setAdminModal(false)} style={{ width: 32, height: 32, borderRadius: "50%", background: "#f3f4f6", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#6b7280" }}>
                <span className="ms" style={{ fontSize: "1rem" }}>close</span>
              </button>
            </div>
            {!editingAdmin && (
              <div style={{ background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: "1rem", padding: ".875rem 1rem", marginBottom: "1.25rem", display: "flex", alignItems: "flex-start", gap: ".75rem" }}>
                <span className="ms" style={{ color: "#2563eb", fontSize: "1.125rem", flexShrink: 0, marginTop: "1px" }}>info</span>
                <p style={{ fontSize: ".8125rem", color: "#1e40af", margin: 0, lineHeight: 1.5 }}>A temporary password will be auto-generated. Accounts start as <strong>Not Activated</strong>.</p>
              </div>
            )}
            <form onSubmit={submitAdmin} style={{ display: "flex", flexDirection: "column", gap: ".875rem" }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: ".875rem" }}>
                {[["Username *", "username", "text"], ["Email *", "email", "email"]].map(([l, k, t]) => (
                  <div key={k}>
                    <label style={{ display: "block", fontSize: ".7rem", fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: ".05em", marginBottom: ".375rem" }}>{l}</label>
                    <input type={t} required className="inp" value={adminForm[k]} onChange={(e) => setAdminForm((p) => ({ ...p, [k]: e.target.value }))} />
                  </div>
                ))}
              </div>
              <div>
                <label style={{ display: "block", fontSize: ".7rem", fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: ".05em", marginBottom: ".375rem" }}>Hospital</label>
                <select className="inp" value={adminForm.hospital_id} onChange={(e) => setAdminForm((p) => ({ ...p, hospital_id: e.target.value }))}>
                  <option value="">— None —</option>
                  {hospitals.map((h) => <option key={h.id} value={h.id}>{h.name}{!h.is_active ? " [Not Activated]" : ""}</option>)}
                </select>
              </div>
              <div>
                <label style={{ display: "block", fontSize: ".7rem", fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: ".05em", marginBottom: ".375rem" }}>Activation Status</label>
                <select className="inp" value={adminForm.is_active ? "true" : "false"} onChange={(e) => setAdminForm((p) => ({ ...p, is_active: e.target.value === "true" }))}>
                  <option value="false">Not Activated</option>
                  <option value="true">Activated</option>
                </select>
              </div>
              <div style={{ display: "flex", gap: ".75rem", marginTop: ".25rem" }}>
                <button type="submit" className="bblu" style={{ flex: 1, justifyContent: "center" }}>{editingAdmin ? "Update" : "Create Admin"}</button>
                <button type="button" onClick={() => setAdminModal(false)} className="bgry" style={{ flex: 1 }}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add/Edit Device */}
      {deviceModal && (
        <div className="modal-overlay" style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50, padding: "1rem" }}>
          <div className="card modal-box" style={{ width: "100%", maxWidth: 480, padding: "2rem" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.25rem" }}>
              <h2 style={{ fontWeight: 700, fontSize: "1.25rem", color: "#111827", margin: 0 }}>{editingDevice ? "Edit Device" : "Add Device"}</h2>
              <button onClick={() => setDeviceModal(false)} style={{ width: 32, height: 32, borderRadius: "50%", background: "#f3f4f6", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#6b7280" }}>
                <span className="ms" style={{ fontSize: "1rem" }}>close</span>
              </button>
            </div>
            <form onSubmit={submitDev} style={{ display: "flex", flexDirection: "column", gap: ".875rem" }}>
              <div>
                <label style={{ display: "block", fontSize: ".7rem", fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: ".05em", marginBottom: ".375rem" }}>Device UID *</label>
                <input type="text" required className="inp" placeholder="e.g. DEV-0001" style={{ fontFamily: "monospace" }} value={deviceForm.uid} onChange={(e) => setDeviceForm((p) => ({ ...p, uid: e.target.value }))} />
              </div>
              <div>
                <label style={{ display: "block", fontSize: ".7rem", fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: ".05em", marginBottom: ".375rem" }}>Hospital</label>
                <select className="inp" value={deviceForm.hospital_id} onChange={(e) => setDeviceForm((p) => ({ ...p, hospital_id: e.target.value }))}>
                  <option value="">— No Hospital —</option>
                  {hospitals.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}
                </select>
              </div>
              <div>
                <label style={{ display: "block", fontSize: ".7rem", fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: ".05em", marginBottom: ".375rem" }}>Status</label>
                <select className="inp" value={deviceForm.status} onChange={(e) => setDeviceForm((p) => ({ ...p, status: e.target.value }))}>
                  <option value="offline">Offline</option>
                  <option value="connected">Connected</option>
                </select>
              </div>
              <div style={{ display: "flex", gap: ".75rem", marginTop: ".25rem" }}>
                <button type="submit" className="bblu" style={{ flex: 1, justifyContent: "center" }}>{editingDevice ? "Update" : "Add"}</button>
                <button type="button" onClick={() => setDeviceModal(false)} className="bgry" style={{ flex: 1 }}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminDashboard;
